import { openDB } from 'idb'

const DB_NAME = 'routine-app-mutation-queue'
const DB_VERSION = 1
const STORE = 'mutation_queue'

let dbPromise = null

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex('table', 'table', { unique: false })
          store.createIndex('userId', 'userId', { unique: false })
          store.createIndex('createdAt', 'createdAt', { unique: false })
        }
      },
    })
  }
  return dbPromise
}

const subscribers = new Set()
function notify() {
  for (const cb of subscribers) {
    try {
      cb()
    } catch {
      /* ignore subscriber errors */
    }
  }
}

export function subscribeQueueChanges(cb) {
  subscribers.add(cb)
  return () => subscribers.delete(cb)
}

// Insert collapse logic: if a delete targets a queued-but-not-synced upsert
// with the same id, just remove the upsert. Same if an update targets a
// queued upsert — fold the update payload into the upsert.
async function collapseOrAdd(db, mutation) {
  const tx = db.transaction(STORE, 'readwrite')
  const store = tx.store
  const existing = await store.get(mutation.id)

  if (mutation.op === 'delete' && existing && existing.op === 'upsert') {
    await store.delete(mutation.id)
  } else if (mutation.op === 'update' && existing && existing.op === 'upsert') {
    await store.put({
      ...existing,
      payload: { ...existing.payload, ...mutation.payload },
      createdAt: existing.createdAt,
    })
  } else {
    await store.put(mutation)
  }
  await tx.done
}

export async function enqueueMutation(partial) {
  const db = await getDb()
  const mutation = {
    id: partial.id || (typeof crypto !== 'undefined' ? crypto.randomUUID() : `m-${Date.now()}-${Math.random()}`),
    table: partial.table,
    op: partial.op,
    payload: partial.payload,
    conflictKey: partial.conflictKey,
    userId: partial.userId,
    createdAt: Date.now(),
    attempts: 0,
  }
  await collapseOrAdd(db, mutation)
  notify()
  return mutation
}

export async function getQueue() {
  const db = await getDb()
  const all = await db.getAllFromIndex(STORE, 'createdAt')
  return all
}

export async function getQueueByTable(table) {
  const db = await getDb()
  const all = await db.getAllFromIndex(STORE, 'table', table)
  return all.sort((a, b) => a.createdAt - b.createdAt)
}

export async function removeMutation(id) {
  const db = await getDb()
  await db.delete(STORE, id)
  notify()
}

export async function incrementAttempts(id, errorMessage) {
  const db = await getDb()
  const tx = db.transaction(STORE, 'readwrite')
  const existing = await tx.store.get(id)
  if (existing) {
    await tx.store.put({
      ...existing,
      attempts: (existing.attempts || 0) + 1,
      lastError: errorMessage,
    })
  }
  await tx.done
  notify()
}

export async function clearQueue(userId) {
  const db = await getDb()
  if (userId) {
    const tx = db.transaction(STORE, 'readwrite')
    const all = await tx.store.index('userId').getAll(userId)
    for (const m of all) await tx.store.delete(m.id)
    await tx.done
  } else {
    await db.clear(STORE)
  }
  notify()
}
